import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Typography, Tag, Button, Space, message } from 'antd';
import * as Icon from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { termsActionStart, termsActionError, getTermsAction, expireTermsAction, activateTermsAction } from '../../core/data/redux/modules/termsAndConditions/actions';
import { getTermsById, expireTerms, activateTerms } from '../../services/adminServices/termsAndConditions';
import { parseContentForEditing } from '../../utils/richText';

const { Title, Paragraph, Text } = Typography;

const TermsDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { terms, loading } = useSelector((state: any) => state.termsAndConditions || { terms: [], loading: false });
  const [localTerms, setLocalTerms] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [termsHtml, setTermsHtml] = useState<string>('');

  useEffect(() => {
    fetchTerms();
    // eslint-disable-next-line
  }, [id]);

  const fetchTerms = async () => {
    dispatch(termsActionStart());
    try {
      const res = await getTermsById(id!);
      if (res.error) throw new Error(res.error);
      dispatch(getTermsAction(res.data));
      setLocalTerms(res.data);
      
      // Parse rich text content for display
      if (res.data) {
        const { parsedContent: termsContent } = parseContentForEditing(res.data.terms_conditions_text || '');
        setTermsHtml(termsContent);
      }
    } catch (err: any) {
      dispatch(termsActionError(err.message));
      message.error(err.message);
    }
  };

  const handleExpire = async () => {
    setActionLoading(true);
    try {
      await expireTerms(id!);
      dispatch(expireTermsAction(id!));
      message.success('Terms expired');
      fetchTerms();
    } catch (err: any) {
      message.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivate = async () => {
    setActionLoading(true);
    try {
      await activateTerms(id!);
      dispatch(activateTermsAction(id!));
      message.success('Terms activated');
      fetchTerms();
    } catch (err: any) {
      message.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading || !localTerms) return <Card loading style={{ margin: 24 }} />;

  return (
    <div className="page-wrapper page-settings" style={{ paddingTop: 0 }}>
      <style>
        {`
          .terms-content h1 { font-size: 2em; font-weight: bold; margin: 16px 0 8px 0; }
          .terms-content h2 { font-size: 1.5em; font-weight: bold; margin: 14px 0 6px 0; }
          .terms-content h3 { font-size: 1.25em; font-weight: bold; margin: 12px 0 4px 0; }
          .terms-content h4 { font-size: 1.1em; font-weight: bold; margin: 10px 0 4px 0; }
          .terms-content h5 { font-size: 1em; font-weight: bold; margin: 8px 0 4px 0; }
          .terms-content h6 { font-size: 0.9em; font-weight: bold; margin: 6px 0 4px 0; }
          .terms-content p { margin: 8px 0; }
          .terms-content strong { font-weight: bold; }
          .terms-content em { font-style: italic; }
          .terms-content u { text-decoration: underline; }
          .terms-content s { text-decoration: line-through; }
        `}
      </style>
      <div className="content">
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
            <Title level={4} style={{ margin: 0 }}>Terms & Conditions</Title>
            <div style={{ textAlign: 'right' }}>
              <div style={{ marginBottom: 8 }}>
                <Tag color={localTerms.is_expired ? 'red' : localTerms.is_active ? 'green' : 'orange'}>
                  {localTerms.is_expired ? 'expired' : localTerms.is_active ? 'active' : 'inactive'}
                </Tag>
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                <div>Created: {new Date(localTerms.created_at).toLocaleDateString()}</div>
                <div>Updated: {new Date(localTerms.updated_at).toLocaleDateString()}</div>
              </div>
            </div>
          </div>
          
          <div style={{ marginBottom: 24 }}>
            <Title level={5}>Basic Information</Title>
            <div>
              <div style={{ marginBottom: 8 }}>
                <Text strong>Version:</Text> {localTerms.version}
              </div>
            </div>
          </div>
          
          <div>
            <Title level={5}>Terms & Conditions</Title>
            <div 
              className="terms-content"
              style={{ 
                border: '1px solid #d9d9d9', 
                borderRadius: '6px', 
                padding: '16px', 
                backgroundColor: '#fafafa',
                minHeight: '200px'
              }}
              dangerouslySetInnerHTML={{ __html: termsHtml }}
            />
          </div>
          <Space style={{ marginTop: 32 }}>
            <Link to="/admin/terms-and-conditions/list-terms">
              <Button>Back to List</Button>
            </Link>
            {!localTerms.is_expired && (
              <Button icon={<Icon.StopCircle size={16} />} loading={actionLoading} onClick={handleExpire}>
                Expire
              </Button>
            )}
            {!localTerms.is_active && (
              <Button icon={<Icon.CheckCircle size={16} />} loading={actionLoading} onClick={handleActivate}>
                Activate
              </Button>
            )}
          </Space>
        </Card>
      </div>
    </div>
  );
};

export default TermsDetail; 